import { showDateOnly } from "../../Common/Utility";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export const EXPORT_HEADERS = {
  isPayed: "Bezahlt €",
  studentName: "Auftrag",
  regionId: "Ort (Region)",
  period: "Zeitraum",
  sumOfAmount: "Betrag (€)",
  pdfDate: "PDF-Datum",
  birthDate: "Geburstdatum",
  butNumber: "But-Number",
  rechnungsnummer: "Rechnungsnummer",
  pdfFileName: "PDF-Datei",
  isPartner: "Partner",
  entryDateFrom: "Entry-From",
  entryDateTo: "Entry-To",
};

const formatCurrency = (value) => {
  if (value == null) return "";

  // Convert to fixed decimal string
  const fixedValue = value.toFixed(2);

  // Remove .00 if present
  const formattedValue = fixedValue.endsWith(".00")
    ? fixedValue.slice(0, -3)
    : fixedValue;

  // Format with German thousands separator
  return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €";
};

export const formatRowData = (row, regionData) => {
  const regionName =
    regionData.find((region) => region.id === row.regionId)?.regionName || "";

  return [
    row.isPayed ? "Ja" : "Nein",
    row.studentName || "",
    regionName,
    row.period || "",
    row.sumOfAmount != null ? formatCurrency(row.sumOfAmount) : "",
    row.pdfDate ? showDateOnly(row.pdfDate) : "",
    row.birthDate ? showDateOnly(row.birthDate) : "",
    row.butNumber || "",
    row.rechnungsnummer || "",
    row.pdfFileName || "",
    row.isPartner ? "Ja" : "Nein",
    row.entryDateFrom ? showDateOnly(row.entryDateFrom) : "",
    row.entryDateTo ? showDateOnly(row.entryDateTo) : "",
  ];
};

export const exportToCsv = (rows, regionData) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";

  const headers = Object.values(EXPORT_HEADERS);
  const csvRows = [headers.join(";")];

  rows.forEach((row) => {
    const rowData = formatRowData(row, regionData);
    csvRows.push(rowData.join(";"));
  });

  return csvRows.join("\n");
};

export const downloadCsv = (
  csvString,
  filename = "Abrechnungsübersicht.csv"
) => {
  if (!csvString) return;

  const blob = new Blob(["\ufeff" + csvString], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");

  try {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up the URL object
  } catch (error) {
    console.error("Error downloading CSV:", error);
  }
};

export const handleExport = (rows, regionData, format = "csv") => {
  if (!Array.isArray(rows) || !Array.isArray(regionData)) {
    console.error("Invalid input data for export");
    return;
  }

  try {
    switch (format.toLowerCase()) {
      case "pdf":
        exportToPdf(rows, regionData);
        break;
      case "csv":
      default:
        const csvString = exportToCsv(rows, regionData);
        downloadCsv(csvString);
    }
  } catch (error) {
    console.error("Error during export:", error);
  }
};

const calculateColumnWidths = (headers, rows, pageWidth) => {
  if (!headers || !rows) return [];

  // A3 landscape usable width (420mm - margins)
  const availableWidth = pageWidth - 28; // 28mm for left and right margins

  // Define column width ratios (total should be 100)
  const columnRatios = {
    0: 5, // Bezahlt (smallest, boolean)
    1: 12, // Auftrag (reduced from 15)
    2: 8, // Ort (Region) (reduced from 10)
    3: 10, // Zeitraum
    4: 9, // Betrag
    5: 9, // PDF-Datum
    6: 9, // Geburstdatum
    7: 9, // But-Number
    8: 11, // Rechnungsnummer
    9: 10, // PDF-Datei
    10: 4, // IsPartner
    11: 4, // Entry-From
    12: 4, // Entry-To
  };

  // Calculate actual widths based on ratios and available width
  return headers.map((_, index) => {
    const ratio = columnRatios[index] || 8; // default ratio if not specified
    return (availableWidth * ratio) / 100;
  });
};

export const exportToPdf = (
  rows,
  regionData,
  title = "Abrechnungsübersicht"
) => {
  if (!Array.isArray(rows) || rows.length === 0) return;

  const headers = Object.values(EXPORT_HEADERS);
  const formattedRows = rows.map((row) => formatRowData(row, regionData));

  try {
    // Create PDF document in A3 landscape
    const pdf = new jsPDF("l", "mm", "a3");
    const pageWidth = pdf.internal.pageSize.width;

    // Add title
    pdf.setFontSize(12);
    pdf.text(title, 14, 20);

    // Calculate total amounts
    const totalAmount = rows.reduce(
      (sum, row) => sum + (row.sumOfAmount || 0),
      0
    );
    const totalPayed = rows
      .filter((row) => row.isPayed)
      .reduce((sum, row) => sum + (row.sumOfAmount || 0), 0);

    // Add summary with adjusted font
    pdf.setFontSize(12);
    pdf.text(`Gesamt Bezahlt: ${formatCurrency(totalPayed)}`, 14, 35);
    pdf.text(`Gesamt: ${formatCurrency(totalAmount)}`, 200, 35);

    // Add table
    const columnWidths = calculateColumnWidths(
      headers,
      formattedRows,
      pageWidth
    );

    // Track unpaid rows
    const unpaidRowIndices = new Set();
    formattedRows.forEach((row, index) => {
      if (row[0] === "Nein") {
        unpaidRowIndices.add(index);
      }
    });

    // Use autoTable directly with optimized settings
    autoTable(pdf, {
      head: [headers],
      body: formattedRows,
      startY: 45,
      theme: "grid",
      columnStyles: headers.reduce((styles, header, index) => {
        styles[index] = {
          cellWidth: columnWidths[index],
          halign: getColumnAlignment(index),
          minCellHeight: 8,
          cellPadding: { left: 2, right: 2, top: 2, bottom: 2 },
          overflow: "linebreak",
        };
        return styles;
      }, {}),
      styles: {
        fontSize: 8,
        overflow: "linebreak",
        font: "helvetica",
        minCellHeight: 8,
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
        minCellHeight: 10,
        valign: "middle",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 45, right: 14, bottom: 20, left: 14 },
      didParseCell: function (data) {
        if (data.section === "body") {
          if (unpaidRowIndices.has(data.row.index)) {
            data.cell.styles.textColor = [220, 0, 0]; // Slightly darker red for better visibility
          }
        }
      },
      didDrawPage: (data) => {
        // Add page number at the bottom
        pdf.setFontSize(8);
        pdf.text(
          `Seite ${data.pageNumber} von ${pdf.getNumberOfPages()}`,
          pdf.internal.pageSize.width - 20,
          pdf.internal.pageSize.height - 10,
          { align: "right" }
        );

        // Add date at the bottom
        const currentDate = new Date().toLocaleDateString("de-DE");
        pdf.text(``, 14, pdf.internal.pageSize.height - 10);
      },
    });

    // Save the PDF
    pdf.save("Abrechnungsübersicht.pdf");
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

// Helper function to determine column alignment
const getColumnAlignment = (columnIndex) => {
  // Right align amount columns
  if (columnIndex === 4) return "right";
  // Center align specific columns
  if ([0, 5, 6, 10, 11].includes(columnIndex)) return "center";
  // Left align the rest
  return "left";
};
